import { Error, PinLockErrorCodes } from "@/types/error.definition";
import axios from "axios";
import { setError } from "../store/reducers/errorSlice";
import {
  forceLogin,
  lockSession,
  pinAttemptsExceeded,
  setPinStatus,
} from "../store/reducers/sessionSlice";
import { store } from "../store/store";
import {
  getApplicationApiUrl,
  getDashboardUrl,
  getLoginApiUrl,
  getLoginApplicationUrl,
} from "../utils/config-utils";
import { errorCategoryMapper, isValidErrorFormat } from "../utils/errors-utils";
const backendApiTimeout = 15000;

// Primary API client
const API_CONFIG = {
  timeout: backendApiTimeout,
  ...(getApplicationApiUrl() && { baseURL: getApplicationApiUrl() }),
  withCredentials: true,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
};
// Primary API client
const LOGIN_API_CONFIG = {
  timeout: backendApiTimeout,
  ...(getLoginApiUrl() && { baseURL: getLoginApiUrl() }),
  withCredentials: true,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
};

// Create axios instance
const apiClient = axios.create(API_CONFIG);

// Create axios instance for login API
const loginApiClient = axios.create(LOGIN_API_CONFIG);

/**
 * MED-1621: the PIN lock answers are for the lock screen. Any request can meet them (the server
 * locks the session after the user's lock delay without activity), so the lock state is updated here.
 */
const handlePinLockError = (code: string) => {
  if (code === "SESSION_LOCKED") {
    store.dispatch(lockSession());
  } else if (code === "PIN_REQUIRED") {
    store.dispatch(setPinStatus({ hasPin: false, locked: false }));
  } else if (code === "PIN_ATTEMPTS_EXCEEDED") {
    store.dispatch(pinAttemptsExceeded());
  } else if (code === "PIN_CREATE_REAUTH_REQUIRED") {
    store.dispatch(forceLogin("REAUTH_REQUIRED"));
  } else if (code === "SESSION_REUSE_DETECTED") {
    store.dispatch(forceLogin("SESSION_REUSE"));
  }
};

// Add interceptors to primary instance
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error?.response?.status;

    // PIN lock codes (403 PIN_REQUIRED, 423 SESSION_LOCKED, 401 PIN_ATTEMPTS_EXCEEDED, 400 PIN_*):
    // no generic error modal and no 401/403 redirect — the lock screen deals with them.
    const pinLockError = error?.response?.data;
    if (pinLockError && PinLockErrorCodes.includes(pinLockError.code)) {
      handlePinLockError(pinLockError.code);
      return Promise.reject({ ...errorCategoryMapper(pinLockError as Error) });
    }

    // account/activity (MED-1621) is the fire-and-forget heartbeat of the PIN lock: only the PIN
    // codes above act on it. Any other failure (offline, 5xx, 401/403) opens no modal and does not
    // redirect — an offline user would otherwise get one every minute; the next real call decides.
    if (error?.config?.url?.endsWith("/account/activity")) {
      return Promise.reject({ code: "UNHANDLED_ERROR", category: "UNKNOWN_ERROR" });
    }

    // Redirect to login on 401 (Unauthorized) or 403 (Forbidden)
    if (status === 401 || status === 403) {
      const redirectTo = getDashboardUrl();
      window.location.href = getLoginApplicationUrl(redirectTo);
      return Promise.reject(error);
    }

    const errorResponse = error?.response?.data;
    // payments/open (MED-1321) is a background lookup on the licence and payment pages: a
    // failure only means "no payment in progress", so it never opens the global error modal.
    // The invoice history list (MED-1652) shows its own error state with a retry button.
    // axios rewrites config.url to the full URL before interceptors run, hence endsWith.
    const requestUrl: string | undefined = error?.config?.url;
    const isQuietRequest =
      !!requestUrl && (requestUrl.endsWith("/payments/open") || /\/healthcare-facilities\/[^/]+\/invoices$/.test(requestUrl));
    if (errorResponse && isValidErrorFormat(errorResponse)) {
      // LICENCE_NOT_FOUND is handled by DashboardLayout blocking page — suppress the modal.
      // INVOICE_NOT_FOUND just means there's nothing pending to pay right now — suppress it too.
      // PAYMENT_INTENT_NOT_FOUND (MED-1321): the checkout is already gone — callers treat it as
      // "nothing to cancel / nothing to wait for", so it is not an error for the user either.
      if (
        !isQuietRequest &&
        errorResponse.code !== "LICENCE_NOT_FOUND" &&
        errorResponse.code !== "INVOICE_NOT_FOUND" &&
        errorResponse.code !== "PAYMENT_INTENT_NOT_FOUND"
      ) {
        store.dispatch(setError(errorResponse as Error));
      }
      return Promise.reject({ ...errorCategoryMapper(errorResponse as Error) });
    } else {
      // Handle unformatted errors
      if (!isQuietRequest) {
        store.dispatch(
          setError({
            code: "UNHANDLED_ERROR",
            category: "UNKNOWN_ERROR",
            message: error?.message || "An unexpected error occurred",
          })
        );
      }
      return Promise.reject({
        code: "UNHANDLED_ERROR",
        category: "UNKNOWN_ERROR",
      });
    }
  }
);

// Add interceptors to primary instance
apiClient.interceptors.request.use(
  (config) => config,
  (error) => {
    const errorResponse = error?.response?.data;

    if (errorResponse && isValidErrorFormat(errorResponse)) {
      store.dispatch(setError(errorResponse as Error));
      return Promise.reject({ ...errorCategoryMapper(errorResponse as Error) });
    } else {
      return Promise.reject({
        code: "UNHANDLED_ERROR",
        category: "UNKNOWN_ERROR",
      });
    }
  }
);

loginApiClient.interceptors.request.use(
  (config) => config,
  (error) => {
    const errorResponse = error?.response?.data;
    if (errorResponse && isValidErrorFormat(errorResponse)) {
      store.dispatch(setError(errorResponse as Error));
      return Promise.reject({ ...errorCategoryMapper(errorResponse as Error) });
    } else {
      return Promise.reject({
        code: "UNHANDLED_ERROR",
        category: "UNKNOWN_ERROR",
      });
    }
  }
);

loginApiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;

    // Redirect to login on 401 (Unauthorized) or 403 (Forbidden)
    if (status === 401 || status === 403) {
      const redirectTo = getDashboardUrl();
      window.location.href = getLoginApplicationUrl(redirectTo);
      return Promise.reject(error);
    }

    const errorResponse = error?.response?.data;
    if (errorResponse && isValidErrorFormat(errorResponse)) {
      // Handle PASSWORD_CHANGE_REQUIRED error - redirect to set-password page
      if (errorResponse.code === "PASSWORD_CHANGE_REQUIRED") {
        window.location.href = "/set-password";
        return Promise.reject({ ...errorCategoryMapper(errorResponse as Error) });
      }
      store.dispatch(setError(errorResponse as Error));
      return Promise.reject({ ...errorCategoryMapper(errorResponse as Error) });
    } else {
      // Handle unformatted errors
      store.dispatch(
        setError({
          code: "UNHANDLED_ERROR",
          category: "UNKNOWN_ERROR",
          message: error?.message || "An unexpected error occurred",
        })
      );
      return Promise.reject({
        code: "UNHANDLED_ERROR",
        category: "UNKNOWN_ERROR",
      });
    }
  }
);
export { apiClient, loginApiClient };
